<script setup>
import { computed } from "vue";
import { useToast } from "vue-toastification";
import InputSwitch from "primevue/inputswitch";

import stores from "@/stores";
import api from "@/api";
import { useLoading } from "@/composables/useLoading.js";
import HtmlToast from "@/components/HtmlToast.vue";
import { commonMarkToHtml } from "@/utils/utils.js";
import { InteractionSourceEnum } from "@/utils/constants.js";

import { useDepartmentStore } from "@/stores/department";
import { useUserStore } from "@/stores/user";
import FullPageLoader from "@/components/ui-kit/base/FullPageLoader.vue";

const { setActiveApiAction, removeActiveApiAction } = useLoading();

const toast = useToast();

const orgStore = stores.useOrganizationStore();
const userStore = stores.useUserStore();
const unitStore = stores.useUnitStore();
const deptStore = stores.useDepartmentStore();
const roleStore = stores.useCustomAuthRoleGroupsStore();

const emit = defineEmits([
  "close",
  "removeTicket",
]);

const actingOnTicket = ref(false);
const showConfirm = ref(false);
const isLoading = ref(false);
const showAddUserModal = ref(false);

const formData = ref({
  beneficiary_user_id: null,
  department_id: null,
  add_email_to_user: true,
  send_ai_response: true,
});
const addEmailToUser = (newValue) => {
  formData.value.add_email_to_user = newValue;
};
const sendAiResponse = (newValue) => {
  formData.value.send_ai_response = newValue;
};

const newUnitModalData = ref({
  isVisible: false,
  userId: "",
  isNewUser: false,
  newUserUnitId: "",
});

const {
  allDict: allRolesDict,
  dropdownOptions: rolesOptions,
} = storeToRefs(stores.useCustomAuthRoleGroupsStore());
const {
  allDict: allDepartmentsDict,
  dropdownOptions: departmentDropdownOptions,
} = storeToRefs(deptStore);
const {
  allDict: allUnitsDict,
  dropdownOptions: unitDropdownOptions
} = storeToRefs(unitStore);

const setIsLoading = (state) => {
  isLoading.value = state;
};

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  threadDetails: {
    type: Object,
    default: {},
  },
});

const interactionDetails = computed(() => {
  if (
    props.threadDetails?.source !== InteractionSourceEnum.UNKNOWN_USER_TICKET ||
    props.threadDetails?.interactions?.length != 1
  ) {
    return {};
  }
  return props.threadDetails.interactions[0];
});

const extractedInfo = computed(() => {
  const query = interactionDetails.value.query;
  try {
    const parsed = JSON.parse(query);
    return parsed;
  }
  catch (error) {
    console.error("Error parsing JSON", query);
    return {};
  }
});

watch(
  () => props.threadDetails?.department,
  (val) => {
    if (val) {
      formData.value.department_id = val;
    }
  },
  {
    deep: true,
    immediate: true,
  }
);

watch(
  () => orgStore.myOrgEnablements,
  (enablements) => {
    if (enablements && !enablements.includes("send_unknown_ticket_ai_response")) {
      formData.value.send_ai_response = false;
    }
  },
  {
    deep: true,
    immediate: true,
  }
)

const closeModal = () => {
  if (actingOnTicket.value) return;

  formData.value = {};
  actingOnTicket.value = false;

  emit("close", {});
};

const isFormValid = () => {
  const isValid = (
    !!formData.value.beneficiary_user_id &&
    !!formData.value.department_id
  );
  return isValid;
};

const confirmRejectTicket = () => {
  showConfirm.value = true;
};

const cancelRejectTicket = () => {
  showConfirm.value = false;
};

const rejectTicket = async () => {
  console.log("reject ticket", props.threadDetails);

  setIsLoading(true);
  setActiveApiAction("rejectUnknownUserTicket");
  try {
    await api.interactions.rejectUnknownUserTicket(interactionDetails.value.id);
    toast.success(`Rejected Unknown User Ticket: ${props.threadDetails.short_id}`);
  } catch (e) {
    console.error(e);
    toast.error(`Error rejecting Unknown User Ticket: ${props.threadDetails.short_id}`);
    return false;
  } finally {
    removeActiveApiAction("rejectUnknownUserTicket");
    setIsLoading(false);
  }

  showConfirm.value = false;

  emit(
    "removeTicket",
    {
      "id": props.threadDetails._id,
    },
  );
  closeModal();
};

const acceptTicket = async () => {
  console.log("accept ticket", props.threadDetails, formData.value);

  setIsLoading(true);
  setActiveApiAction("acceptUnknownUserTicket");
  try {
    await api.interactions.acceptUnknownUserTicket(
      interactionDetails.value.id,
      {
        ...(formData.value),
        new_user_email: extractedInfo?.value?.reply_to?.send_response_to || null,
      },
    );
    toast.success(
      {
        component: HtmlToast,
        props: {
          html: `Accepted Unknown User Ticket: <a href="/inbox/${props.threadDetails._id}" style="color: #fff; font-weight: bold;">#${props.threadDetails.short_id}</a>`,
        },
      }, 
      {
        timeout: 20000,
      }
    );
  } catch (e) {
    console.error(e);
    toast.error(`Error accepting Unknown User Ticket: ${props.threadDetails.short_id}`);
    return false;
  } finally {
    removeActiveApiAction("acceptUnknownUserTicket");
    setIsLoading(false);
  }

  emit(
    "removeTicket",
    {
      "id": props.threadDetails._id,
    },
  );
  closeModal();
};

const onCreateUserLinkClick = () => {
  console.log('show create user modal');
  showAddUserModal.value = true;
};

const extractUserPropertiesForApi = (user) => {
  return {
    ...(user.id ? { id: user.id } : {}),
    first_name: user.first_name,
    last_name: user.last_name,
    status: user.status,
    role_ids: user.role_ids.length
      ? user.role_ids.map((id) => parseInt(id))
      : [],
    department_ids: user.department_ids,
    unit_ids: user.unit_ids,
    title: user.title,
    primary_email: user.primary_email,
    organization_id: userStore.authUserOrganizationId,
    secondary_email: user.secondary_email,
    primary_phone_number: user.primary_phone_number,
    secondary_phone_number: user.secondary_phone_number,
    slack_id: user.slack_id,
    msteams_id: user.msteams_id,
    meta_id: user.meta_id,
    wordpress_id: user.wordpress_id,
    created_by: user.created_by,
    created: user.created,
    last_login: user.last_login,
    alc_tags: user?.alc_tags || null,
  };
};

const _createUser = async (payload) => {
  setActiveApiAction("createUser");
  const modifiedPayload = extractUserPropertiesForApi(payload);
  try {
    const createdUserResponse = await api.user.createUser(modifiedPayload);
    toast.success("Successfully created User");
    userStore.fetchAllUsers(false);
    return createdUserResponse;
  } catch (e) {
    console.error(e);
    toast.error("Error creating User");
    return false;
  } finally {
    removeActiveApiAction("createUser");
  }
};

const createUser = async (payload) => {
  console.log("create user", payload);

  const createdUserResponse = await _createUser(payload);
  console.log("created user response", createdUserResponse);
  if (createdUserResponse === false) {
    return;
  }

  // select the created User
  formData.value.beneficiary_user_id = createdUserResponse.id;

  // close the Add User modal
  showAddUserModal.value = false;
};

const createUnitForUser = (userId, isNewUser) => {
  newUnitModalData.value = {
    isVisible: true,
    userId: userId,
    isNewUser: isNewUser,
  };
};

</script>

<template>
  <FullPageLoader :show="isLoading"></FullPageLoader>
  <BModal
    class="unknown-user-ticket-modal"
    title="Unknown User"
    hide-footer
    :model-value="modelValue"
    no-close-on-backdrop
    no-close-on-esc
    @close="closeModal"
  >
    <div class="modal-body-container">
      <div class="">
        <div class="half-width">
          <label
            class="label w-100 p-0 text-left text-decoration-none text-neutral-350"
          >
            Ticket #
          </label>
          {{ threadDetails.short_id }}
        </div>

        <div class="half-width">
          <label
            class="label w-100 p-0 text-left text-decoration-none text-neutral-350"
          >
            Received
          </label>
          {{ threadDetails.formattedCreatedAt }}
        </div>
      </div>

      <div class="sender mt-3">
        <div style="width: calc((100% - 70px) / 2); display: inline-block; vertical-align: top;">
          <label
            class="label w-100 p-0 text-left text-decoration-none text-neutral-350"
          >
            Sender Email
          </label>
          <div class="text-value">
            {{ extractedInfo?.reply_to.send_response_to || "--" }}
          </div>
        </div>

        <div class="arrow">
        </div>

        <div style="width: calc((100% - 70px) / 2); display: inline-block;">
          <div class="d-flex">
            <div>
              <label
                class="label w-100 p-0 text-left text-decoration-none text-neutral-350"
              >
                Link User
              </label>
            </div>

            <div class="flex-fill"/>

            <div class="font-size-xs">
              <a
                href="#"
                @click.stop="onCreateUserLinkClick"
              >Create User</a>
            </div>
          </div>
          <SelectInput
            :options="userStore.loading ? [] : userStore.allSupportableUsersWithEmailDropdownOptions"
            :tryToFindNameInOptionsOnFirstLoad="true"
            :reduce="(option) => option.value"
            v-model="formData.beneficiary_user_id"
          />
        </div>
      </div>

      <div class="mt-3">
        <div class="half-width" style="vertical-align: top;">
          <label
            class="label w-100 p-0 text-left text-decoration-none text-neutral-350"
          >
            Sent To
          </label>
          <div class="text-value">
            {{ extractedInfo?.allow_unknown_users_emails?.[0] || "--" }}
          </div>
        </div>

        <div class="half-width">
          <label
            class="label w-100 p-0 text-left text-decoration-none text-neutral-350"
          >
            Department
          </label>
          <SelectInput
            :options="deptStore.loading ? [] : deptStore.dropdownOptions"
            :tryToFindNameInOptionsOnFirstLoad="true"
            :reduce="(option) => option.value"
            v-model="formData.department_id"
          />
        </div>
      </div>

      <div class="mt-3">
        <label
          class="label w-100 p-0 text-left text-decoration-none text-neutral-350"
        >
           Subject
        </label>
        {{ extractedInfo?.subject || "--" }}
      </div>

      <div class="mt-3">
        <label
          class="label w-100 p-0 text-left text-decoration-none text-neutral-350"
        >
          Email Message
        </label>
        <div class="ticket-body">
          <iframe
            class="message-iframe"
            :srcdoc="commonMarkToHtml(extractedInfo.processed)"
          />
        </div>
      </div>

      <div class="my-mt-3 d-flex justify-content-end">
        <div class="toggle-container-outer">
          <div class="toggle-container">
            <div class="toggle-label">
              <span>Add Sender Email to Chosen User</span>
            </div>
            <InputSwitch
              class="toggle-switch"
              :disabled="!isFormValid() || actingOnTicket"
              :modelValue="formData.add_email_to_user"
              @update:modelValue="addEmailToUser"
            />
          </div>
        </div>
      </div>

      <div class="mt-2 d-flex justify-content-end">
        <div class="toggle-container-outer">
          <div class="toggle-container">
            <div class="toggle-label">
              <span>Send AI Response to User</span>
            </div>
            <InputSwitch
              class="toggle-switch"
              :disabled="!isFormValid() || actingOnTicket"
              :modelValue="formData.send_ai_response"
              @update:modelValue="sendAiResponse"
            />
          </div>
        </div>
      </div>

      <div class="mt-2 d-flex justify-content-end">
        <custom-button
          :disabled="actingOnTicket"
          has-font-semi-bold
          button-type="white"
          @click="confirmRejectTicket()"
        >
          Reject
        </custom-button>

        <div class="flex-fill"/>

        <custom-button
          :disabled="!isFormValid() || actingOnTicket"
          has-font-semi-bold
          button-type="blue"
          @click="acceptTicket()"
        >
          Accept
        </custom-button>
      </div>
    </div>
  </BModal>

  <BModal
    v-model="showConfirm"
    title="Reject Unknown User Ticket"
    no-close-on-backdrop
    hide-header-close
    hide-footer
    centered
  >
    <p class="mb-3">
      Please confirm that you wish to reject <strong>Ticket #{{threadDetails.short_id}}</strong>
    </p>
    <div class="d-flex justify-content-end gap-2">
      <custom-button
        :disabled="actingOnTicket"
        button-type="white"
        has-font-semi-bold
        @click="rejectTicket()"
      >
        OK
      </custom-button>
      <custom-button
        :disabled="actingOnTicket"
        button-type="blue"
        @click="cancelRejectTicket()"
      >
        Cancel
      </custom-button>

    </div>
  </BModal>

  <add-user-modal
    v-if="showAddUserModal"
    v-model="showAddUserModal"
    :role-permissions-options="rolesOptions"
    :department-options="departmentDropdownOptions"
    :units-options="unitDropdownOptions"
    :unitInitialValue="newUnitModalData.newUserUnitId"
    :initialPrimaryEmailAddress="extractedInfo.reply_to?.send_response_to"
    :initialRoleId="roleStore.getIdFromCode('view_only')"
    :enableInviteUser="false"
    :showChannels="false"
    @create-user="createUser"
    @addNewUnit="createUnitForUser(null, true)"
    @isLoading="setIsLoading"
  />

  <AddUnitModal
    v-model="newUnitModalData.isVisible"
    @isLoading="setIsLoading"
  />

</template>

<style lang="scss">
.unknown-user-ticket-modal {
  font-size: 14px;

  .label {
    font-size: 14px;
    color: #595959;
    margin-bottom: 0.625rem;
  }

  .modal-body {
    padding: 1rem;
  }

  .modal-dialog {
    --bs-modal-width: 800px;
  }

  .modal-body-container > div {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }

  .first-row {
    padding-top: 1rem;
    padding-bottom: 1rem;
    margin-bottom: 1rem;
  }

  .text-value {
    height: 2.5rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .sender {
    .arrow {
      display: inline-block;
      width: 50px;
      height: 8px;
      background: #000000;
      clip-path: polygon(0 37.5%, 88.5% 37.5%, 88.5% 0, 100% 50%, 88.5% 100%, 88.5% 62.5%, 0 62.5%);
      margin: 0 10px;
    }
  }

  .flex-fill {
    flex-grow: 1;
  }

  .half-width {
    width: 50%;
    display: inline-block;
  }

  .message-iframe {
    width: 100%;
    height: 100%;
  }

  .ticket-body {
    height: 300px;
    width: 100%;
    overflow-y: scroll;
  }

  .toggle-container-outer {
    display: flex;
    flex-direction: column;
    justify-content: center;

    .toggle-container {
      display: flex;

      .toggle-label {
        margin-right: 1rem;
        color: #8c8c8c;
      }
      .toggle-switch {
        //margin-right: 1rem;
      }

      .p-toggleswitch-checked {
        --p-toggleswitch-checked-background: var(--primary-250);
        --p-toggleswitch-checked-hover-background: var(--primary-200);
      }
    }
  }
}
</style>
